import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { TwoFactorSetupResponse } from '@/lib/definitions/api.types'
import { UserService } from '@/lib/services/UserService'
import { Notify } from '@/lib/misc/Notify'
import { parseResponseErrorsAndNotify } from '@/lib/misc/Utils'
import Button from '@/components/Button'
import LoadingSpinner from '@/components/LoadingSpinner'
import { object, string } from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import TotpCodeInput from '@/components/TotpCodeInput'
import { REFRESH_APP_INFO, useApp } from '@/lib/provider/AppProvider'
import GuestLayout from '@/layouts/GuestLayout'

const userService = new UserService()

const schema = object({
  token: string()
    .required('Token is required')
    .matches(/^\d{6}$/, 'Code must be 6 digits'),
})

interface TwoFactorAuthFormData {
  token: string
}

interface TwoFactorEnforcementDialogProps {
  isOpen: boolean
  allowSkip: boolean
  tfaEnforcementType: string
}

const TwoFactorEnforcementDialog = ({
  isOpen,
  allowSkip,
  tfaEnforcementType,
}: TwoFactorEnforcementDialogProps) => {
  const [twoFactorSetupContent, setTwoFactorSetupContent] =
    useState<TwoFactorSetupResponse | null>(null)
  const [, dispatchApp] = useApp()
  const {
    mutate: setupTwoFactorAuthentication,
    isPending: isSettingUpTwoFactorAuthentication,
  } = useMutation({
    mutationFn: () => userService.setupTwoFactorAuth(),
    onError: (error: any) => {
      parseResponseErrorsAndNotify(error)
    },
    onSuccess: async (response) => {
      setTwoFactorSetupContent(response.data)
    },
  })

  const {
    mutate: confirmTwoFactorToken,
    isPending: isConfirmingTwoFactorToken,
  } = useMutation({
    mutationFn: (formData: TwoFactorAuthFormData) =>
      userService.setupTwoFactorAuthConfirm(formData.token ?? ''),
    onError: (error: any) => {
      parseResponseErrorsAndNotify(error)
    },
    onSuccess: async () => {
      Notify.success('Two-factor authentication has been enabled')
      setTwoFactorSetupContent(null)

      dispatchApp({ type: REFRESH_APP_INFO })
    },
  })

  const { mutate: deferEnforcement, isPending: isDeferringEnforcement } =
    useMutation({
      mutationFn: () => userService.deferTwoFactorEnforcement(),
      onError: (error: any) => {
        parseResponseErrorsAndNotify(error)
      },
      onSuccess: () => {
        dispatchApp({ type: REFRESH_APP_INFO })
      },
    })

  const form = useForm<TwoFactorAuthFormData>({
    resolver: yupResolver(schema),
  })

  const enforcementTitle = twoFactorSetupContent
    ? 'Connecting your authenticator app'
    : tfaEnforcementType === 'organization-wide'
      ? 'Two-factor authentication is required'
      : allowSkip
        ? 'Two-factor authentication is on the way'
        : "Let's add two-factor authentication"

  const enforcementDescription =
    tfaEnforcementType === 'organization-wide'
      ? 'Your organization requires two-factor authentication for all users. Set it up below to continue accessing your account.'
      : allowSkip
        ? 'Two-factor authentication is now part of signing in for every account. Connect an authenticator app when you are ready—it usually takes just a minute or two. You will need it before your next login, and you can skip once during this visit if you are in the middle of something.'
        : 'Two-factor authentication is now part of signing in for every account. After your password, you will enter a short code from an authenticator app. Set yours up below to continue—it only takes a moment and helps keep your account safe.'

  const authenticatorHint =
    'You will use an app such as Google Authenticator, Authy, or 1Password to generate a six-digit code when you sign in from a new place. If you already use one of those, you are all set.'

  const isBusyInitial =
    !twoFactorSetupContent && isSettingUpTwoFactorAuthentication

  if (!isOpen) return null

  return (
    <GuestLayout forceShowLogo containerClassName="max-w-xl">
      <div className="p-8" data-cy="two-factor-enforcement-dialog">
        <div className="space-y-2">
          <h2 className="mt-5 text-center text-heading-2 text-neutral-90">
            {enforcementTitle}
          </h2>
          {!!enforcementDescription && !twoFactorSetupContent && (
            <p className="text-neutral-50 mt-6 leading-6">
              {enforcementDescription}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-4 mt-6">
          {isSettingUpTwoFactorAuthentication && (
            <section className="rounded-xl border border-hairline bg-surface px-5 py-5 shadow-btn flex justify-center py-10">
              <LoadingSpinner className="size-8" />
            </section>
          )}

          {twoFactorSetupContent && (
            <>
              <section className="rounded-xl bg-neutral-10/50 px-5 py-5">
                <div className="text-center">
                  <img
                    src={twoFactorSetupContent.qr_code_url}
                    alt="QR Code"
                    className="mx-auto mb-4 max-w-48"
                  />
                  <p className="mb-3 text-neutral-50 leading-6">
                    Scan this QR code with your authenticator app, or add the
                    secret key below if you prefer typing it in.
                  </p>
                  <div
                    className="rounded-lg bg-neutral-10 p-3 text-center font-mono text-xs break-all"
                    data-cy="two-factor-secret-key"
                  >
                    {twoFactorSetupContent.secret_key}
                  </div>
                  <p className="mt-2 text-xs text-neutral-50">
                    Treat this key like a password—only use it inside your
                    trusted authenticator app.
                  </p>
                </div>
              </section>

              <section className="rounded-xl bg-neutral-10/50 px-5 py-5">
                <h3 className="mb-4 text-center text-neutral-60">
                  Confirm the pairing
                </h3>
                <form
                  className="space-y-4"
                  onSubmit={form.handleSubmit((data) =>
                    confirmTwoFactorToken(data)
                  )}
                >
                  <div className="flex justify-center items-center">
                    <Controller
                      name="token"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <TotpCodeInput
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          disabled={isConfirmingTwoFactorToken}
                          errors={[fieldState.error?.message]}
                          label="Enter the 6-digit code your app shows right now"
                          inputDataCyPrefix="tfa-enforcement-code"
                          formErrorsDataCy="form-errors-tfa-enforcement-code"
                        />
                      )}
                    />
                  </div>

                  <Button
                    style="primary"
                    disabled={isConfirmingTwoFactorToken}
                    type="submit"
                    className="w-full items-center justify-center"
                    cy="tfa-enforcement-confirm-btn"
                  >
                    {isConfirmingTwoFactorToken
                      ? 'Checking your code…'
                      : 'Verify and finish'}
                  </Button>
                </form>
              </section>
            </>
          )}

          {!isBusyInitial && !twoFactorSetupContent ? (
            <section className="">
              <h3 className="mb-3 text-neutral-90 text-xl">
                What you will need
              </h3>
              <p className="mb-5 leading-6 text-neutral-50">
                {authenticatorHint}
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => setupTwoFactorAuthentication()}
                  style="primary"
                  className="w-full items-center justify-center"
                  cy="tfa-enforcement-start-btn"
                >
                  Turn on two-factor authentication
                </Button>
                {allowSkip && (
                  <Button
                    onClick={() => deferEnforcement()}
                    style="secondary"
                    className="w-full items-center justify-center"
                    disabled={isDeferringEnforcement}
                    cy="tfa-enforcement-skip-btn"
                  >
                    {isDeferringEnforcement
                      ? 'Skipping…'
                      : 'Skip for now (this visit only)'}
                  </Button>
                )}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </GuestLayout>
  )
}

export default TwoFactorEnforcementDialog
